/**
 * DailyStatsLogs - "Статистика (Логи)"
 *
 * Props:
 *   accountId      string   - marketplace account UUID
 *   accountName    string   - display name
 *   marketplace    string   - "ozon" | "wildberries"
 *   workspaceTasks array    - [{id, title, closed_at}]
 */
import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useApi } from '../../../hooks/useApi';
import MilestoneLineChart from '../../../components/MilestoneLineChart';
import UnifiedTable from '../../../components/UnifiedTable/UnifiedTable';
import { filterBySearch } from '../../../utils/search';

export const OZON_COLS = [
    { key: 'revenue',             label: 'Заказано на сумму',       fmt: 'money', isRate: false },
    { key: 'ordered_units',       label: 'Заказано товаров',         fmt: 'int',   isRate: false },
    { key: 'hits_view_search',    label: 'Показы в поиске',          fmt: 'int',   isRate: false },
    { key: 'hits_view_pdp',       label: 'Показы на карточке',       fmt: 'int',   isRate: false },
    { key: 'hits_view',           label: 'Всего показов',            fmt: 'int',   isRate: false },
    { key: 'hits_tocart_search',  label: 'В корзину из поиска',      fmt: 'int',   isRate: false },
    { key: 'hits_tocart_pdp',     label: 'В корзину из карточки',    fmt: 'int',   isRate: false },
    { key: 'hits_tocart',         label: 'Всего в корзину',          fmt: 'int',   isRate: false },
    { key: 'session_view_search', label: 'Сессий в поиске',          fmt: 'int',   isRate: false },
    { key: 'session_view_pdp',    label: 'Сессий на карточке',       fmt: 'int',   isRate: false },
    { key: 'session_view',        label: 'Всего сессий',             fmt: 'int',   isRate: false },
    { key: 'conv_tocart_search',  label: 'Конверсия из поиска %',    fmt: 'pct',   isRate: true  },
    { key: 'conv_tocart_pdp',     label: 'Конверсия из карточки %',  fmt: 'pct',   isRate: true  },
    { key: 'conv_tocart',         label: 'Общая конверсия %',        fmt: 'pct',   isRate: true  },
];

// Extra opt-in columns (Ozon only), toggled by dedicated checkboxes rather than
// the "Столбцы" show/hide dropdown — they pull data from other modules (Реклама,
// Остатки) so they stay out of the always-fetched default column set.
const DRR_COL = { key: 'drr', label: 'ДРР %', fmt: 'pct', isRate: true };
// Остатки — two separate columns (FBO / FBS), both toggled by the single
// "Остатки" checkbox. The backend returns per-SKU {stock_fbo, stock_fbs,
// stock_total}; showing only the combined total made FBS invisible whenever
// an account keeps most of its goods on FBO.
const STOCK_FBO_COL = { key: 'stock_fbo', label: 'Остаток FBO', fmt: 'int', isRate: false };
const STOCK_FBS_COL = { key: 'stock_fbs', label: 'Остаток FBS', fmt: 'int', isRate: false };
const STOCK_COLS = [STOCK_FBO_COL, STOCK_FBS_COL];
const STOCK_KEYS = ['stock_fbo', 'stock_fbs'];

export const WB_COLS = [
    { key: 'open_count',               label: 'Открытий',              fmt: 'int',   isRate: false },
    { key: 'cart_count',               label: 'В корзину',             fmt: 'int',   isRate: false },
    { key: 'order_count',              label: 'Заказано товаров',      fmt: 'int',   isRate: false },
    { key: 'order_sum',                label: 'Сумма заказов',         fmt: 'money', isRate: false },
    { key: 'buyout_count',             label: 'Выкупов',               fmt: 'int',   isRate: false },
    { key: 'buyout_sum',               label: 'Сумма выкупов',         fmt: 'money', isRate: false },
    { key: 'buyout_percent',           label: 'Выкуп %',               fmt: 'pct',   isRate: true  },
    { key: 'add_to_cart_conversion',   label: 'Конверсия в корзину %', fmt: 'pct',   isRate: true  },
    { key: 'cart_to_order_conversion', label: 'Конверсия в заказ %',   fmt: 'pct',   isRate: true  },
    { key: 'add_to_wishlist_count',    label: 'Избранное',             fmt: 'int',   isRate: false },
];

const numFmt = new Intl.NumberFormat('ru-RU');
const moneyFmt = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 });

const fmt = (val, type) => {
    if (val === null || val === undefined) return '-';
    const n = Number(val);
    if (isNaN(n)) return '-';
    if (type === 'money') return moneyFmt.format(n);
    if (type === 'pct') return `${n.toFixed(2)}%`;
    return numFmt.format(Math.round(n));
};

const fmtDate = (s) => {
    if (!s) return '';
    const [y, m, d] = s.split('-');
    return `${d}.${m}.${y}`;
};

function addDays(iso, n) {
    const d = new Date(iso);
    d.setDate(d.getDate() + n);
    return d.toISOString().split('T')[0];
}

function median(vals) {
    if (!vals.length) return 0;
    const sorted = [...vals].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

function computeSummary(daily, allCols) {
    if (!daily.length) return { total: {}, avg: {}, med: {} };
    const total = {};
    const avg = {};
    const med = {};
    for (const col of allCols) {
        const vals = daily.map((d) => Number(d[col.key]) || 0);
        const sum = vals.reduce((a, b) => a + b, 0);
        const n = vals.length;
        total[col.key] = col.isRate ? (n ? sum / n : 0) : sum;
        avg[col.key] = n ? sum / n : 0;
        med[col.key] = median(vals);
    }
    return { total, avg, med };
}

function applyPreset(p) {
    const to = new Date();
    to.setDate(to.getDate() - 1);
    const from = new Date(to);
    if (p === 'week') from.setDate(from.getDate() - 6);
    if (p === 'month') from.setDate(from.getDate() - 29);
    if (p === 'quarter') from.setDate(from.getDate() - 89);
    const d = (x) => x.toISOString().split('T')[0];
    return { from: d(from), to: d(to) };
}

function recomputeRateFields(row, isOzon) {
    if (isOzon) {
        const hvs = Number(row.hits_view_search || 0);
        const hvsPdp = Number(row.hits_view_pdp || 0);
        const hv = Number(row.hits_view || 0);
        const hts = Number(row.hits_tocart_search || 0);
        const htp = Number(row.hits_tocart_pdp || 0);
        const ht = Number(row.hits_tocart || 0);
        row.conv_tocart_search = hvs > 0 ? (hts / hvs) * 100 : 0;
        row.conv_tocart_pdp = hvsPdp > 0 ? (htp / hvsPdp) * 100 : 0;
        row.conv_tocart = hv > 0 ? (ht / hv) * 100 : 0;
        // ДРР общий = расход на рекламу / выручка (recomputed from summed raw
        // fields after merging accounts/days, never averaged — same rule as
        // the conversion rates above).
        const adSpend = Number(row.ad_spend || 0);
        const revenue = Number(row.revenue || 0);
        row.drr = revenue > 0 ? (adSpend / revenue) * 100 : 0;
    } else {
        const open = Number(row.open_count || 0);
        const cart = Number(row.cart_count || 0);
        const order = Number(row.order_count || 0);
        const buyout = Number(row.buyout_count || 0);
        row.add_to_cart_conversion = open > 0 ? (cart / open) * 100 : 0;
        row.cart_to_order_conversion = cart > 0 ? (order / cart) * 100 : 0;
        row.buyout_percent = order > 0 ? (buyout / order) * 100 : 0;
    }
}

// Hidden-column state is shared between the summary table and the per-day
// detail table (one "Столбцы" menu drives both), so it lives here instead of
// inside UnifiedTable's own uncontrolled hook. The storage key matches the
// format useColumnVisibility uses, so it stays compatible with the shared hook.
function hiddenColsStorageKey(tableId) {
    return `ut-hidden-columns:${tableId}`;
}

function readHiddenCols(tableId) {
    try {
        const raw = window.localStorage.getItem(hiddenColsStorageKey(tableId));
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

function writeHiddenCols(tableId, keys) {
    try {
        if (!keys || !keys.length) window.localStorage.removeItem(hiddenColsStorageKey(tableId));
        else window.localStorage.setItem(hiddenColsStorageKey(tableId), JSON.stringify(keys));
    } catch {
        // localStorage unavailable (private mode / quota) — no-op
    }
}

export default function DailyStatsLogs({
    accountId,
    accountName = '',
    marketplace,
    workspaceTasks = [],
    projectMode = false,
    projectCabinets = [],
}) {
    const { authFetch, API_BASE_URL } = useApi();
    const isOzon = marketplace === 'ozon';
    const allCols = isOzon ? OZON_COLS : WB_COLS;
    const accountIds = projectMode && projectCabinets.length > 0 ? projectCabinets.map((c) => c.id) : accountId ? [accountId] : [];

    const [periodFrom, setPeriodFrom] = useState('');
    const [periodTo, setPeriodTo] = useState('');
    const [groupBy, setGroupBy] = useState('sku'); // 'sku' | 'model' (Ozon only)
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [chartMetric, setChartMetric] = useState(allCols[0].key);
    const [selectedRow, setSelectedRow] = useState('total');
    const [showMilestones, setShowMilestones] = useState(true);
    const [showDrr, setShowDrr] = useState(false);
    const [showStock, setShowStock] = useState(false);
    const [stockUnavailable, setStockUnavailable] = useState(false);
    // Which of FBO/FBS have no snapshot at all for the selected cabinets — their
    // column shows 0 for every row, which reads as "нет товара" rather than
    // "нет синхронизации" unless we say so explicitly.
    const [missingStockKinds, setMissingStockKinds] = useState([]);
    const tableId = `daily-stats-${marketplace}`;
    const detailTableId = `daily-stats-detail-${marketplace}`;
    const [hiddenCols, setHiddenCols] = useState(() => readHiddenCols(tableId));
    const [sortCol, setSortCol] = useState(null);
    const [sortDir, setSortDir] = useState('desc');
    const [artSearch, setArtSearch] = useState('');
    const [artFilter, setArtFilter] = useState([]);
    const [artPickerOpen, setArtPickerOpen] = useState(false);

    useEffect(() => {
        setArtFilter([]);
        setSelectedRow('total');
    }, [groupBy]);

    const hiddenColsSet = useMemo(() => new Set(hiddenCols), [hiddenCols]);
    const cols = useMemo(() => {
        const base = allCols.filter((c) => !hiddenColsSet.has(c.key));
        const extra = [];
        if (isOzon && showDrr) extra.push(DRR_COL);
        if (isOzon && showStock) extra.push(...STOCK_COLS);
        return [...base, ...extra];
    }, [allCols, hiddenColsSet, isOzon, showDrr, showStock]);
    // Every column the table knows about, including the ones the user hid via the
    // shared "Столбцы" menu. `cols` (above) is the visible subset and still drives
    // the chart metric picker and the CSV export.
    const tableCols = useMemo(() => {
        const extra = [];
        if (isOzon && showDrr) extra.push(DRR_COL);
        if (isOzon && showStock) extra.push(...STOCK_COLS);
        return [...allCols, ...extra];
    }, [allCols, isOzon, showDrr, showStock]);
    const daily = data?.daily ?? [];
    const rawArticles = data?.articles ?? [];

    // Group by "Название модели" (assortment attribute). Data is already fully
    // loaded client-side, so grouping is a local aggregation — no extra request.
    // Absolute metrics are summed; rate metrics (conv_tocart*, buyout_percent, ...)
    // are recomputed from the summed counts via recomputeRateFields, never averaged.
    const articles = useMemo(() => {
        if (groupBy !== 'model' || !isOzon) return rawArticles;
        const modelMap = new Map();
        for (const art of rawArticles) {
            const model = art.model_name || 'Без модели';
            let entry = modelMap.get(model);
            if (!entry) {
                entry = {
                    sku_id: model,
                    offer_id: '',
                    article_name: model,
                    product_name: model,
                    model_name: model,
                    sku_count: 0,
                    total: {},
                    dailyMap: new Map(),
                };
                modelMap.set(model, entry);
            }
            entry.sku_count += 1;
            for (const col of allCols) {
                if (col.isRate) continue;
                entry.total[col.key] = (entry.total[col.key] || 0) + Number(art.total?.[col.key] || 0);
            }
            entry.total.ad_spend = (entry.total.ad_spend || 0) + Number(art.total?.ad_spend || 0);
            for (const key of STOCK_KEYS) {
                entry.total[key] = (entry.total[key] || 0) + Number(art.total?.[key] || 0);
            }
            for (const day of art.daily || []) {
                const dayAgg = entry.dailyMap.get(day.date) || { date: day.date };
                for (const col of allCols) {
                    if (col.isRate) continue;
                    dayAgg[col.key] = (dayAgg[col.key] || 0) + Number(day[col.key] || 0);
                }
                dayAgg.ad_spend = (dayAgg.ad_spend || 0) + Number(day.ad_spend || 0);
                entry.dailyMap.set(day.date, dayAgg);
            }
        }
        return Array.from(modelMap.values())
            .map((entry) => {
                recomputeRateFields(entry.total, isOzon);
                const dailyRows = Array.from(entry.dailyMap.values())
                    .map((row) => {
                        // stock_fbo/stock_fbs are "right now" snapshots, not per-day
                        // metrics — carry the model's current stock through onto every
                        // day row, set once this model's accumulation is final.
                        for (const key of STOCK_KEYS) row[key] = entry.total[key];
                        recomputeRateFields(row, isOzon);
                        return row;
                    })
                    .sort((a, b) => String(a.date).localeCompare(String(b.date)));
                const { dailyMap, ...rest } = entry;
                return { ...rest, daily: dailyRows };
            })
            .sort((a, b) => (b.total.revenue || 0) - (a.total.revenue || 0));
    }, [rawArticles, groupBy, isOzon, allCols]);

    const hasData = daily.length > 0;
    // Look in `cols` (which includes the opt-in ДРР/Остатки columns), not just
    // allCols — otherwise picking "Остаток FBS" on the chart showed the label of
    // the first default column instead.
    const currentCol = cols.find((c) => c.key === chartMetric) || allCols.find((c) => c.key === chartMetric) || allCols[0];

    const nonZeroArticles = useMemo(
        () => articles.filter((a) => allCols.some((c) => !c.isRate && (a.total[c.key] || 0) !== 0)),
        [articles, allCols],
    );

    const filteredArticles = useMemo(() => {
        if (!artFilter.length && !artSearch) return nonZeroArticles;
        return nonZeroArticles.filter((a) => {
            if (artFilter.length && !artFilter.includes(a.sku_id)) return false;
            const searchIn = [a.offer_id, a.article_name, a.product_name, a.sku_id].join(' ').toLowerCase();
            if (artSearch && !searchIn.includes(artSearch.toLowerCase())) return false;
            return true;
        });
    }, [nonZeroArticles, artFilter, artSearch]);

    const articleFilterOptions = useMemo(() => {
        return filterBySearch(nonZeroArticles, artSearch).slice(0, 200);
    }, [nonZeroArticles, artSearch]);

    const sortedArticles = useMemo(() => {
        if (!sortCol) return filteredArticles;
        return [...filteredArticles].sort((a, b) => {
            if (sortCol === 'label') {
                const av = String(a.offer_id || a.article_name || a.sku_id || '');
                const bv = String(b.offer_id || b.article_name || b.sku_id || '');
                return sortDir === 'asc' ? av.localeCompare(bv, 'ru') : bv.localeCompare(av, 'ru');
            }
            if (sortCol === 'sku_id' || sortCol === 'product_name') {
                const av = String(a[sortCol] || '');
                const bv = String(b[sortCol] || '');
                return sortDir === 'asc' ? av.localeCompare(bv, 'ru') : bv.localeCompare(av, 'ru');
            }
            const avRaw = a.total?.[sortCol];
            const bvRaw = b.total?.[sortCol];
            const avNum = Number(avRaw);
            const bvNum = Number(bvRaw);
            const av = Number.isFinite(avNum) ? avNum : 0;
            const bv = Number.isFinite(bvNum) ? bvNum : 0;
            if (av === bv) {
                const aLabel = String(a.offer_id || a.article_name || a.sku_id || '');
                const bLabel = String(b.offer_id || b.article_name || b.sku_id || '');
                return aLabel.localeCompare(bLabel, 'ru');
            }
            return sortDir === 'asc' ? av - bv : bv - av;
        });
    }, [filteredArticles, sortCol, sortDir]);

    const summaryCols = useMemo(
        () => (isOzon && showDrr ? [...allCols, DRR_COL] : allCols),
        [allCols, isOzon, showDrr],
    );
    const { total: itogoBase, avg: avgBase, med: medBase } = useMemo(
        () => computeSummary(daily, summaryCols),
        [daily, summaryCols],
    );

    // stock_fbo/stock_fbs are point-in-time snapshots (not day-scoped), so they
    // can't be derived from the `daily` rows the way computeSummary works —
    // aggregate them straight from the per-article totals instead.
    const stockSummary = useMemo(() => {
        const empty = { total: 0, avg: 0, med: 0 };
        if (!isOzon || !showStock) return { stock_fbo: empty, stock_fbs: empty };
        const out = {};
        for (const key of STOCK_KEYS) {
            const vals = articles.map((a) => Number(a.total?.[key]) || 0);
            const total = vals.reduce((a, b) => a + b, 0);
            out[key] = { total, avg: vals.length ? total / vals.length : 0, med: median(vals) };
        }
        return out;
    }, [isOzon, showStock, articles]);

    const pickStock = useCallback(
        (key, row) => {
            const s = stockSummary[key] || { total: 0, avg: 0, med: 0 };
            return row === 'avg' ? s.avg : row === 'median' ? s.med : s.total;
        },
        [stockSummary],
    );

    const withStock = (base, row) => {
        if (!isOzon || !showStock) return base;
        const out = { ...base };
        for (const key of STOCK_KEYS) out[key] = pickStock(key, row);
        return out;
    };
    const itogo = withStock(itogoBase, 'total');
    const avg = withStock(avgBase, 'avg');
    const med = withStock(medBase, 'median');

    const chartData = useMemo(() => {
        if (!daily.length) return [];
        // stock_fbo/stock_fbs are "right now" snapshots, not day-scoped — plot
        // them as a constant line at the current level rather than reading a
        // per-day field that doesn't exist on the account-wide `daily` rows.
        if (STOCK_KEYS.includes(chartMetric) && (selectedRow === 'total' || selectedRow === 'avg' || selectedRow === 'median')) {
            const stockVal = pickStock(chartMetric, selectedRow);
            return daily.map((d) => ({ date: d.date, value: stockVal }));
        }
        if (selectedRow === 'total' || selectedRow === 'avg' || selectedRow === 'median') {
            return daily.map((d) => ({ date: d.date, value: Number(d[chartMetric]) || 0 }));
        }
        const art = articles.find((a) => a.sku_id === selectedRow);
        if (!art) return daily.map((d) => ({ date: d.date, value: Number(d[chartMetric]) || 0 }));
        return art.daily.map((d) => ({ date: d.date, value: Number(d[chartMetric]) || 0 }));
    }, [daily, articles, selectedRow, chartMetric, pickStock]);

    const detailRows = useMemo(() => {
        if (selectedRow !== 'total' && selectedRow !== 'avg' && selectedRow !== 'median') {
            const art = articles.find((a) => a.sku_id === selectedRow);
            return art ? art.daily : [];
        }
        if (isOzon && showStock) {
            return daily.map((d) => {
                const row = { ...d };
                for (const key of STOCK_KEYS) row[key] = pickStock(key, selectedRow);
                return row;
            });
        }
        return daily;
    }, [selectedRow, daily, articles, isOzon, showStock, pickStock]);

    const milestones = useMemo(() => {
        if (!showMilestones || !workspaceTasks.length) return [];
        return workspaceTasks
            .filter((t) => t.closed_at)
            .map((t) => ({
                date: addDays(t.closed_at.split('T')[0], 2),
                label: t.title,
                color: '#3b82f6',
            }));
    }, [showMilestones, workspaceTasks]);

    const loadData = async () => {
        if (!periodFrom || !periodTo) {
            alert('Укажите период');
            return;
        }
        if (accountIds.length === 0) return;

        setLoading(true);
        setSelectedRow('total');
        setArtFilter([]);
        try {
            const dailyMap = new Map();
            const articleMap = new Map();

            for (const accId of accountIds) {
                const url = `${API_BASE_URL}/api/v1/daily-stats/${accId}?date_from=${periodFrom}&date_to=${periodTo}`;
                const resp = await authFetch(url);
                const d = resp && typeof resp === 'object' && !Array.isArray(resp) ? resp : { daily: Array.isArray(resp) ? resp : [], articles: [] };

                for (const day of d.daily || []) {
                    const date = day.date;
                    if (!date) continue;
                    const agg = dailyMap.get(date) || { date };
                    for (const col of allCols) {
                        agg[col.key] = Number(agg[col.key] || 0) + Number(day[col.key] || 0);
                    }
                    // ad_spend/drr are opt-in (Реклама module) — always merge the raw
                    // spend so the ДРР checkbox can be toggled without a re-fetch.
                    agg.ad_spend = Number(agg.ad_spend || 0) + Number(day.ad_spend || 0);
                    dailyMap.set(date, agg);
                }

                for (const article of d.articles || []) {
                    const sku = String(article.sku_id || '');
                    if (!sku) continue;
                    const agg = articleMap.get(sku) || {
                        sku_id: sku,
                        offer_id: article.offer_id || '',
                        article_name: article.article_name || '',
                        product_name: article.product_name || '',
                        model_name: article.model_name || '',
                        total: {},
                        dailyMap: new Map(),
                    };
                    for (const col of allCols) {
                        agg.total[col.key] = Number(agg.total[col.key] || 0) + Number(article.total?.[col.key] || 0);
                    }
                    agg.total.ad_spend = Number(agg.total.ad_spend || 0) + Number(article.total?.ad_spend || 0);
                    for (const day of article.daily || []) {
                        const date = day.date;
                        if (!date) continue;
                        const dayAgg = agg.dailyMap.get(date) || { date };
                        for (const col of allCols) {
                            dayAgg[col.key] = Number(dayAgg[col.key] || 0) + Number(day[col.key] || 0);
                        }
                        dayAgg.ad_spend = Number(dayAgg.ad_spend || 0) + Number(day.ad_spend || 0);
                        agg.dailyMap.set(date, dayAgg);
                    }
                    articleMap.set(sku, agg);
                }
            }

            // Остатки — current stock is a point-in-time snapshot (not date-ranged),
            // so it's fetched separately only when the checkbox is on, keyed by SKU.
            const stockMap = new Map();
            let stockFetchAttempted = false;
            // FBO and FBS come from two independent snapshots: an account can have a
            // fresh FBO one and no FBS one at all (FBS stock used to be written only by
            // a manual button press). `as_of_*` is present only when that snapshot
            // exists, which is how we tell "нет синхронизации" apart from a real 0.
            let sawFboSnapshot = false;
            let sawFbsSnapshot = false;
            if (isOzon && showStock) {
                stockFetchAttempted = true;
                for (const accId of accountIds) {
                    try {
                        const stockUrl = `${API_BASE_URL}/api/v1/ozon-ops/${accId}/stock/current-by-sku`;
                        const stockResp = await authFetch(stockUrl);
                        const items = stockResp && typeof stockResp === 'object' ? stockResp.items || {} : {};
                        for (const [sku, info] of Object.entries(items)) {
                            if (info?.as_of_fbo) sawFboSnapshot = true;
                            if (info?.as_of_fbs) sawFbsSnapshot = true;
                            const prev = stockMap.get(sku) || { stock_fbo: 0, stock_fbs: 0 };
                            stockMap.set(sku, {
                                stock_fbo: prev.stock_fbo + Number(info?.stock_fbo || 0),
                                stock_fbs: prev.stock_fbs + Number(info?.stock_fbs || 0),
                            });
                        }
                    } catch (e) {
                        // Non-fatal — stock column just stays empty for this account.
                        console.warn('stock/current-by-sku failed', e);
                    }
                }
            }
            setMissingStockKinds(
                stockFetchAttempted && stockMap.size > 0
                    ? [
                        ...(sawFboSnapshot ? [] : ['FBO']),
                        ...(sawFbsSnapshot ? [] : ['FBS']),
                    ]
                    : []
            );
            // Stock is only ever written by an explicit FBS/FBO sync (see
            // ozon_fbs_fbo_service.get_current_stock_by_sku) — an account that has
            // never run one legitimately has nothing to show here. Surface that as
            // a message instead of letting the column silently read 0 everywhere.
            setStockUnavailable(stockFetchAttempted && stockMap.size === 0);

            const mergedDaily = Array.from(dailyMap.values())
                .map((row) => {
                    recomputeRateFields(row, isOzon);
                    return row;
                })
                .sort((a, b) => String(a.date).localeCompare(String(b.date)));

            const mergedArticles = Array.from(articleMap.values()).map((a) => {
                const total = { ...a.total };
                recomputeRateFields(total, isOzon);
                if (isOzon && showStock) {
                    // stock_fbo/stock_fbs are "right now" numbers keyed by SKU — try the
                    // raw sku string, then a numeric-normalised form, then the offer_id
                    // (backend keys each entry by BOTH sku and offer_id).
                    const info =
                        stockMap.get(a.sku_id) ??
                        stockMap.get(String(Number(a.sku_id))) ??
                        (a.offer_id ? stockMap.get(String(a.offer_id)) : undefined);
                    total.stock_fbo = Number(info?.stock_fbo || 0);
                    total.stock_fbs = Number(info?.stock_fbs || 0);
                }
                const dailyRows = Array.from(a.dailyMap.values())
                    .map((row) => {
                        recomputeRateFields(row, isOzon);
                        if (isOzon && showStock) {
                            for (const key of STOCK_KEYS) row[key] = total[key];
                        }
                        return row;
                    })
                    .sort((x, y) => String(x.date).localeCompare(String(y.date)));
                return {
                    sku_id: a.sku_id,
                    offer_id: a.offer_id,
                    article_name: a.article_name,
                    product_name: a.product_name,
                    model_name: a.model_name,
                    total,
                    daily: dailyRows,
                };
            });

            setData({ daily: mergedDaily, articles: mergedArticles });
        } catch (e) {
            alert(`Ошибка загрузки: ${e.message}`);
            setData({ daily: [], articles: [] });
        } finally {
            setLoading(false);
        }
    };

    // Unlike ad_spend/ДРР (always merged into every load so that checkbox can
    // toggle instantly), stock is fetched via a separate opt-in request only
    // when showStock is on — see loadData above. That means flipping the
    // "Остатки" checkbox AFTER data is already on screen previously did
    // nothing until the user pressed "Загрузить" again: the column would
    // render with blank/zero values and no warning, because stockUnavailable
    // is only recomputed inside loadData. Auto-refetch here so the checkbox
    // behaves the same way the ДРР one does.
    const hasLoadedOnceRef = useRef(false);
    useEffect(() => {
        if (data) hasLoadedOnceRef.current = true;
    }, [data]);
    useEffect(() => {
        if (isOzon && hasLoadedOnceRef.current && !loading) {
            loadData();
        }
    }, [showStock]);

    // UnifiedTable owns the "Столбцы" menu; hidden state is controlled here so
    // that both tables, the chart metric picker and the CSV export stay in sync.
    const handleHiddenColsChange = useCallback((next) => {
        setHiddenCols(next);
        writeHiddenCols(tableId, next);
    }, [tableId]);

    // UnifiedTable calls onSortChange(key, dir); keep this page's rule that a new
    // column starts descending (biggest numbers first) instead of ascending.
    const handleSort = useCallback((key) => {
        setSortCol((prev) => {
            if (prev === key) {
                setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
                return key;
            }
            setSortDir('desc');
            return key;
        });
    }, []);

    const toggleArticleFilter = (sku) => {
        setArtFilter((prev) => (
            prev.includes(sku) ? prev.filter((s) => s !== sku) : [...prev, sku]
        ));
    };

    const addAllFoundArticles = () => {
        setArtFilter((prev) => {
            const next = new Set(prev);
            for (const row of articleFilterOptions) next.add(row.sku_id);
            return Array.from(next);
        });
    };

    const selectedLabel = () => {
        if (selectedRow === 'total') return 'Итого';
        if (selectedRow === 'avg') return 'Среднее';
        if (selectedRow === 'median') return 'Медиана';
        const art = articles.find((a) => a.sku_id === selectedRow);
        if (!art) return selectedRow;
        return art.offer_id || art.article_name || art.sku_id;
    };

    // Flat rows for UnifiedTable: the three summary rows (Итого / Среднее /
    // Медиана) are pinned at the top of the data array and stay there because
    // sorting is done by us (serverSort) over `sortedArticles` only. Metric
    // values are spread onto the row itself so the shared cell/copy/export
    // machinery can read them by column key.
    const tableRows = useMemo(() => ([
        { ...itogo, _key: 'total',  _label: 'Итого',   _bold: true,  _summary: true, sku_id: '', product_name: '' },
        { ...avg,   _key: 'avg',    _label: 'Среднее', _bold: false, _summary: true, sku_id: '', product_name: '' },
        { ...med,   _key: 'median', _label: 'Медиана', _bold: false, _summary: true, sku_id: '', product_name: '' },
        ...sortedArticles.map((a) => ({
            ...a.total,
            _key: a.sku_id,
            _label: a.offer_id || a.article_name || a.sku_id,
            _bold: false,
            _summary: false,
            sku_id: a.sku_id || '',
            product_name: a.product_name || '',
        })),
    ]), [itogo, avg, med, sortedArticles]);

    const detailTableRows = useMemo(
        () => detailRows.map((r, i) => ({ ...r, _key: `${r.date || i}` })),
        [detailRows],
    );

    const metricColumn = useCallback((c, locked) => ({
        key: c.key,
        header: c.label,
        width: 140,
        minWidth: 80,
        align: 'right',
        sortable: true,
        // ДРР / Остатки are driven by their own checkboxes, not the "Столбцы"
        // menu — lock them so the menu can't switch them off behind the checkbox.
        hideable: !locked,
        render: (v) => fmt(v, c.fmt),
    }), []);

    const isOptInCol = useCallback(
        (key) => key === DRR_COL.key || STOCK_KEYS.includes(key),
        [],
    );

    const summaryColumns = useMemo(() => {
        const out = [{
            key: 'label',
            header: 'Строка',
            width: 180,
            minWidth: 120,
            sticky: true,
            hideable: false,
            sortable: true,
            render: (_v, row) => (
                <span
                    style={{
                        fontWeight: row._bold || selectedRow === row._key ? 600 : undefined,
                        color: selectedRow === row._key ? 'var(--color-primary, #3b82f6)' : undefined,
                        whiteSpace: 'nowrap',
                    }}
                    title={row._label}
                >
                    {selectedRow === row._key ? '> ' : ''}{row._label}
                </span>
            ),
        }];
        if (groupBy !== 'model') {
            out.push({ key: 'sku_id', header: 'SKU', width: 110, minWidth: 80, sortable: true, render: (v) => v || '' });
            out.push({ key: 'product_name', header: 'Название', width: 220, minWidth: 120, sortable: true, render: (v) => v || '' });
        }
        // Pass the FULL column set (not the visible `cols`): UnifiedTable does the
        // hiding itself, and a column dropped from this array would also vanish
        // from the "Столбцы" menu, leaving no way to bring it back.
        for (const c of tableCols) out.push(metricColumn(c, isOptInCol(c.key)));
        return out;
    }, [tableCols, groupBy, selectedRow, metricColumn, isOptInCol]);

    const detailColumns = useMemo(() => ([
        {
            key: 'date',
            header: 'Дата',
            width: 120,
            minWidth: 90,
            sticky: true,
            hideable: false,
            render: (v) => fmtDate(v),
        },
        ...tableCols.map((c) => metricColumn(c, isOptInCol(c.key))),
    ]), [tableCols, metricColumn, isOptInCol]);

    const selectedRowKeys = useMemo(() => [selectedRow], [selectedRow]);

    const downloadCsv = useCallback(() => {
        const metricHeaders = cols.map((c) => c.label);
        const headers = ['Строка', 'SKU', 'Название', ...metricHeaders];

        const fmtCsv = (val, type) => {
            if (val === null || val === undefined) return '';
            const n = Number(val);
            if (isNaN(n)) return '';
            if (type === 'pct') return n.toFixed(2);
            if (type === 'money') return n.toFixed(2);
            return Math.round(n);
        };

        const rowsToCsv = tableRows.map((row) => [
            row._label,
            row.sku_id,
            row.product_name,
            ...cols.map((c) => fmtCsv(row[c.key], c.fmt)),
        ]);

        const escape = (v) => {
            const s = String(v ?? '');
            return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
        };

        const csv = [headers, ...rowsToCsv].map((r) => r.map(escape).join(',')).join('\n');
        const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `stats_${periodFrom}_${periodTo}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }, [tableRows, cols, periodFrom, periodTo]);

    const summaryToolbarActions = useMemo(
        () => [{ label: '↓ CSV', title: 'Скачать CSV', onClick: downloadCsv }],
        [downloadCsv],
    );

    return (
        <div className="ozon-analytics-panel">
            <div className="analytics-controls">
                <div className="analytics-periods">
                    <div className="period-row">
                        <label className="period-label">Период:</label>
                        <input type="date" value={periodFrom} onChange={(e) => setPeriodFrom(e.target.value)} className="period-input" />
                        <span className="period-separator">-</span>
                        <input type="date" value={periodTo} onChange={(e) => setPeriodTo(e.target.value)} className="period-input" />
                    </div>
                </div>
                <div className="analytics-presets">
                    <span className="presets-label">Быстрый выбор:</span>
                    {['week', 'month', 'quarter'].map((p) => (
                        <button
                            key={p}
                            type="button"
                            className="btn btn-sm btn-ghost"
                            onClick={() => {
                                const r = applyPreset(p);
                                setPeriodFrom(r.from);
                                setPeriodTo(r.to);
                            }}
                        >
                            {p === 'week' ? 'Неделя' : p === 'month' ? 'Месяц' : 'Квартал'}
                        </button>
                    ))}
                </div>
                {isOzon && (
                    <div className="analytics-group-toggle">
                        <button
                            type="button"
                            className={`btn btn-sm ${groupBy === 'sku' ? 'btn-primary' : 'btn-ghost'}`}
                            onClick={() => setGroupBy('sku')}
                        >
                            По артикулам
                        </button>
                        <button
                            type="button"
                            className={`btn btn-sm ${groupBy === 'model' ? 'btn-primary' : 'btn-ghost'}`}
                            onClick={() => setGroupBy('model')}
                        >
                            По моделям
                        </button>
                    </div>
                )}
                {isOzon && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }} title="Добавить столбец ДРР (доля рекламных расходов от выручки) из модуля Реклама">
                            <input type="checkbox" checked={showDrr} onChange={(e) => setShowDrr(e.target.checked)} />
                            ДРР
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }} title="Уточнить текущие остатки FBO/FBS по SKU">
                            <input type="checkbox" checked={showStock} onChange={(e) => setShowStock(e.target.checked)} />
                            Остатки
                        </label>
                        {showStock && stockUnavailable && (
                            <span style={{ fontSize: 12, color: 'var(--color-warning, #b45309)' }}>
                                Остатки не синхронизированы для этого кабинета — запустите синхронизацию в разделе «Логистика» (FBS/FBO).
                            </span>
                        )}
                        {showStock && !stockUnavailable && missingStockKinds.length > 0 && (
                            <span style={{ fontSize: 12, color: 'var(--color-warning, #b45309)' }}>
                                {`Остатки ${missingStockKinds.join(' и ')} ни разу не синхронизировались для этого кабинета — в столбц${missingStockKinds.length > 1 ? 'ах' : 'е'} будут нули. Запустите синхронизацию в разделе «Логистика».`}
                            </span>
                        )}
                    </div>
                )}
                <div className="analytics-actions">
                    <button type="button" className="btn btn-primary" onClick={loadData} disabled={loading || accountIds.length === 0}>
                        {loading ? 'Загрузка...' : 'Загрузить'}
                    </button>
                </div>
            </div>

            <p className="muted" style={{ fontSize: 12, margin: '4px 0 12px' }}>
                Это сохраненная статистика по дням из локальной базы. Данные могут немного отличаться от базовой статистики маркетплейса.
            </p>

            {data !== null && (
                <>
                    {!hasData ? (
                        <div className="empty">Нет данных за выбранный период. История накапливается с момента подключения кабинета.</div>
                    ) : (
                        <>
                            {articles.length > 0 && (
                                <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                    <span style={{ fontSize: 13, color: 'var(--color-muted)' }}>{groupBy === 'model' ? 'Модели:' : 'Артикулы:'}</span>
                                    <input
                                        type="text"
                                        placeholder="Артикул, SKU, название..."
                                        value={artSearch}
                                        onChange={(e) => setArtSearch(e.target.value)}
                                        style={{
                                            padding: '4px 8px',
                                            borderRadius: 4,
                                            border: '1px solid var(--color-border)',
                                            fontSize: 13,
                                            width: 180,
                                            background: 'var(--color-input-bg)',
                                            color: 'var(--color-text)',
                                        }}
                                    />
                                    <button type="button" className="btn btn-sm btn-ghost" onClick={() => setArtPickerOpen((v) => !v)}>
                                        {artPickerOpen ? 'Скрыть список' : 'Выбрать несколько'}
                                    </button>
                                    {!!articleFilterOptions.length && (
                                        <button type="button" className="btn btn-sm btn-ghost" onClick={addAllFoundArticles}>
                                            Добавить найденные
                                        </button>
                                    )}
                                    {artFilter.length > 0 && (
                                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                            {artFilter.map((sku) => {
                                                const art = articles.find((a) => a.sku_id === sku);
                                                return (
                                                    <span key={sku} style={{ padding: '2px 8px', background: '#3b82f620', borderRadius: 4, fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                                                        {art?.offer_id || art?.article_name || sku}
                                                        <button
                                                            type="button"
                                                            onClick={() => setArtFilter((f) => f.filter((s) => s !== sku))}
                                                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 14, color: '#888' }}
                                                        >
                                                            x
                                                        </button>
                                                    </span>
                                                );
                                            })}
                                            <button type="button" className="btn btn-sm btn-ghost" onClick={() => setArtFilter([])}>
                                                Сбросить
                                            </button>
                                        </div>
                                    )}
                                    {artPickerOpen && (
                                        <div
                                            style={{
                                                width: '100%',
                                                maxHeight: 220,
                                                overflow: 'auto',
                                                border: '1px solid var(--color-border)',
                                                borderRadius: 6,
                                                background: 'var(--color-surface)',
                                                padding: 8,
                                            }}
                                        >
                                            {articleFilterOptions.length === 0 ? (
                                                <div style={{ fontSize: 12, color: 'var(--color-muted)' }}>Ничего не найдено</div>
                                            ) : (
                                                articleFilterOptions.map((a) => {
                                                    const checked = artFilter.includes(a.sku_id);
                                                    const label = a.offer_id || a.article_name || a.sku_id;
                                                    return (
                                                        <label key={a.sku_id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '4px 0', cursor: 'pointer' }}>
                                                            <input
                                                                type="checkbox"
                                                                checked={checked}
                                                                onChange={() => toggleArticleFilter(a.sku_id)}
                                                            />
                                                            <span style={{ fontSize: 12, lineHeight: 1.35 }}>
                                                                <span>{label}</span>
                                                                <span style={{ display: 'block', color: 'var(--color-muted)' }}>
                                                                    SKU: {a.sku_id}{a.product_name ? ` · ${a.product_name}` : ''}
                                                                </span>
                                                            </span>
                                                        </label>
                                                    );
                                                })
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="analytics-summary" style={{ marginBottom: 8 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 6 }}>
                                    <h3 style={{ margin: 0 }}>
                                        График - {currentCol.label}
                                        <span style={{ fontWeight: 400, fontSize: 13, color: 'var(--color-muted)', marginLeft: 8 }}>
                                            ({selectedLabel()})
                                        </span>
                                    </h3>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', marginLeft: 'auto' }}>
                                        <input type="checkbox" checked={showMilestones} onChange={(e) => setShowMilestones(e.target.checked)} />
                                        Отмечать вехи
                                    </label>
                                </div>
                                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 8 }}>
                                    {cols.map((c) => (
                                        <button key={c.key} type="button" className={`btn btn-sm ${chartMetric === c.key ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setChartMetric(c.key)}>
                                            {c.label}
                                        </button>
                                    ))}
                                </div>
                                <MilestoneLineChart data={chartData} milestones={milestones} color="#3b82f6" label={currentCol.label} milestoneOffset={0} />
                            </div>

                            <div className="analytics-summary" style={{ marginBottom: 12 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                    <h3 style={{ margin: 0 }}>Общая статистика</h3>
                                </div>
                                <UnifiedTable
                                    tableId={tableId}
                                    columns={summaryColumns}
                                    data={tableRows}
                                    getRowKey={(row) => row._key}
                                    onRowClick={(row) => setSelectedRow(row._key)}
                                    selectedRows={selectedRowKeys}
                                    sortKey={sortCol}
                                    sortDir={sortDir}
                                    onSortChange={handleSort}
                                    serverSort
                                    showPagination={false}
                                    hiddenColumns={hiddenCols}
                                    onHiddenColumnsChange={handleHiddenColsChange}
                                    toolbarActions={summaryToolbarActions}
                                    onExportCsv={false}
                                    exportFilename={`stats_${periodFrom}_${periodTo}`}
                                />
                                {nonZeroArticles.length > 0 && (
                                    <p style={{ fontSize: 12, color: 'var(--color-muted)', margin: '6px 0 0' }}>
                                        Нажмите на строку, чтобы показать ее данные на графике.
                                        {` Показано ${sortedArticles.length} из ${nonZeroArticles.length} артикулов.`}
                                        {articles.length > nonZeroArticles.length && ` (${articles.length - nonZeroArticles.length} скрыто с нулями)`}
                                    </p>
                                )}
                            </div>

                            <div className="analytics-detail">
                                <h3>
                                    Детализация по дням ({detailRows.length})
                                    {selectedRow !== 'total' && selectedRow !== 'avg' && selectedRow !== 'median' && (
                                        <span style={{ fontWeight: 400, fontSize: 13, color: 'var(--color-muted)', marginLeft: 8 }}>
                                            - {selectedLabel()}
                                        </span>
                                    )}
                                </h3>
                                <UnifiedTable
                                    tableId={detailTableId}
                                    columns={detailColumns}
                                    data={detailTableRows}
                                    getRowKey={(row) => row._key}
                                    pageSize="all"
                                    showPagination={false}
                                    hiddenColumns={hiddenCols}
                                    onHiddenColumnsChange={handleHiddenColsChange}
                                    exportFilename={`stats_detail_${periodFrom}_${periodTo}`}
                                />
                            </div>
                        </>
                    )}
                </>
            )}
        </div>
    );
}
